import { hoverOutlineColor, MULTI_SELECT_TEMPORARY_ID, selectedOutlineColor } from '@/editor/constant/entity';
import { IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { createMultiModelData } from '@/editor/data/multiModel/utils';
import { getPureBoxes, getPureBoxesByData } from '@/editor/data/utils';
import BoxesSelectedEntity from '@/editor/entities/selectedEntity/boxesSelectedEntity';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { TGlobalStores } from '@/editor/stores';
import { EEntityType, ISelected } from '@/editor/types';
import { isFurnitureOrAssembly } from '@/editor/utils/select';
import * as THREE from 'three';
import { CommonController3d } from '../commonController';
import { rayEntity3d } from '../rayEntity';

enum EMouseButton {
  EMPTY = -1,
  LEFT = 0,
  MIDDLE = 1,
  RIGHT = 2
}

export default class MultiSelect3d extends CommonController3d {
  private _raycaster = new THREE.Raycaster();
  private _mouse = new THREE.Vector2();
  private _firstDraw = true;
  private _mouseDownX = 0;
  private _mouseDownY = 0;
  private _mouseDownButton = EMouseButton.EMPTY;
  private _mouseDownEntityInfo: ISelected = { entityId: '', type: EEntityType.EMPTY };
  private _multiModelData: IMultiModelData | null = null;
  private _hoverBoxes = new BoxesSelectedEntity();
  private _selectedBoxes = new BoxesSelectedEntity();
  private _isComplete = false;
  private _disposeSet: Set<() => void> = new Set();

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this._disposeSet
      .add(editorEvent.on(EditorEventName.KEYBOARD_PRESS_UPDATE, this.handleComplete))
      .add(
        editorEvent.on(EditorEventName.ENTITY_CLEAR, () => {
          this._multiModelData = null;
          this.updateHoverBoxes();
          this.updateSelectedBoxes();
          this.notifyMultiSelectData();
        })
      )
      // 交接进来的那一次左键按下，走正常的 mouseDown 流程
      .add(editorEvent.on(EditorEventName.MULTI_SELECT_TAKEOVER_MOUSE_DOWN, (event) => this.mouseDown(event)));
    const currentHover = this._gs.editorStateStore.hoverEntity;

    if (
      currentHover &&
      ![EEntityType.Furniture, EEntityType.Assembly, EEntityType.MultiModel].includes(currentHover.type)
    ) {
      this._gs.editorStateStore.clearHoverEntity();
    }
    this.initSelectedData();
  }

  protected override mouseDown(event: MouseEvent) {
    this._mouseDownButton = event.button;

    if (event.button === EMouseButton.LEFT) {
      this._mouseDownX = event.clientX;
      this._mouseDownY = event.clientY;
      this._mouseDownEntityInfo = this.rayEntity(event);
      if (this._firstDraw) {
        this._firstDraw = false;

        // 开始框选时，检查当前selected是否为非可多选模型，不是则要清空
        const currentSelected = this._gs.editorStateStore.selected;
        if (
          currentSelected &&
          ![EEntityType.Furniture, EEntityType.Assembly, EEntityType.MultiModel].includes(currentSelected.type)
        ) {
          this._gs.editorStateStore.clearSelected();
        }
      }
    } else if (event.button === EMouseButton.RIGHT || event.button === EMouseButton.MIDDLE) {
      this._gs.editorStateStore.dragging = true;
    }
  }

  protected override mouseMove(event: MouseEvent) {
    switch (this._mouseDownButton) {
      case EMouseButton.LEFT:
        this._gs.editorStateStore.rotating = true;
        return;
      case EMouseButton.EMPTY: {
        const entityInfo = this.rayEntity(event);
        this._mouseDownEntityInfo = entityInfo;
        this.updateHoverBoxes(entityInfo);
        return;
      }
    }
  }

  protected override mouseUp(event: MouseEvent) {
    if (event.button === EMouseButton.LEFT) {
      this._gs.editorStateStore.rotating = false;

      const entityInfo = this.rayEntity(event);
      const samePoint = this._mouseDownX === event.clientX && this._mouseDownY === event.clientY;
      const isSameEntity = entityInfo.entityId === this._mouseDownEntityInfo.entityId;

      if (samePoint && isSameEntity && isFurnitureOrAssembly(entityInfo)) {
        this.toggleSelected(entityInfo);
      }
    } else if (event.button === EMouseButton.RIGHT || event.button === EMouseButton.MIDDLE) {
      this._gs.editorStateStore.dragging = false;
    }

    this._mouseDownButton = EMouseButton.EMPTY;
    this.handleComplete();
  }

  private rayEntity(event: MouseEvent) {
    return rayEntity3d(this._gs, event, this._raycaster, this._mouse, { resolveMultiModel: false });
  }

  /** 3D 下隔墙不可选中，接管 2D 带过来的多选时把隔墙剔掉，包围盒跟着重算 */
  private dropSeparateWalls(multiModelData: IMultiModelData | null) {
    if (!multiModelData) return null;

    const modelInfos = multiModelData.modelInfos.filter((modelInfo) => modelInfo.type !== EEntityType.SeparateWall);
    if (modelInfos.length === multiModelData.modelInfos.length) return multiModelData;

    return modelInfos.length ? createMultiModelData(modelInfos) : null;
  }

  private initSelectedData() {
    const currentSelected = this._gs.editorStateStore.selected;
    // 隔墙被剔光时 _multiModelData 会是 null，但全局选中一样要清，否则隔墙会留在场景里选着
    let hasTakenOverSelected = false;

    switch (currentSelected.type) {
      case EEntityType.Furniture:
      case EEntityType.Assembly:
        this._multiModelData = createMultiModelData(
          this._gs.multiModelOperation.transformModelInfos([currentSelected])
        );
        hasTakenOverSelected = !!this._multiModelData;
        break;
      case EEntityType.MultiModel: {
        const multiModelData = this._gs.getDataByIdType('', EEntityType.MultiModel) ?? null;
        this._multiModelData = this.dropSeparateWalls(multiModelData);
        hasTakenOverSelected = !!multiModelData;
        break;
      }
      default:
        this._multiModelData = null;
        break;
    }

    if (hasTakenOverSelected) {
      this._gs.editorStateStore.clearSelected();
    }

    this.updateSelectedBoxes();
    this.notifyMultiSelectData();
  }

  /** 把当前多选内容同步给参数面板。多选期间 selected 是空的，面板只能靠这条通知刷新 */
  private notifyMultiSelectData() {
    editorEvent.emit(EditorEventName.MULTI_SELECT_DATA_CHANGE, this._multiModelData?.modelInfos ?? []);
  }

  private toggleSelected(selected: ISelected) {
    const modelInfos = this._gs.multiModelOperation.transformModelInfos([selected], this._multiModelData?.modelInfos);

    if (modelInfos.length) {
      this._multiModelData = createMultiModelData(modelInfos);
    } else {
      this._multiModelData = null;
    }

    if (this._multiModelData?.modelInfos.find((info) => info.id === this._gs.editorStateStore.hoverEntity.entityId)) {
      this._gs.editorStateStore.clearHoverEntity();
    }

    this.updateSelectedBoxes();
    this.notifyMultiSelectData();
  }

  private updateHoverBoxes(selected?: ISelected) {
    this._hoverBoxes.clear();

    if (!selected) {
      return;
    }

    if (!isFurnitureOrAssembly(selected)) return;

    const boxesData = getPureBoxes(selected);
    if (!boxesData) return;

    this._hoverBoxes.create({ ...boxesData, linewidth: 1, color: hoverOutlineColor });
  }

  private updateSelectedBoxes() {
    this._selectedBoxes.clear();

    if (!this._multiModelData) return;

    const boxesData = getPureBoxesByData(EEntityType.MultiModel, this._multiModelData);
    if (!boxesData) return;

    this._selectedBoxes.create({ ...boxesData, linewidth: 1, color: selectedOutlineColor });
  }

  private applySelected() {
    if (this._multiModelData) {
      if (this._multiModelData.modelInfos.length === 1) {
        const [modelInfo] = this._multiModelData.modelInfos;
        this._gs.editorStateStore.setSelected({ entityId: modelInfo.id, type: modelInfo.type });
      } else {
        this._gs.editorStateStore.setSelected(
          { entityId: MULTI_SELECT_TEMPORARY_ID, type: EEntityType.MultiModel },
          { multiModelData: this._multiModelData }
        );
      }
    }
  }

  private handleComplete = () => {
    if (
      this._gs.keyboardStore.isMultiSelectModifierPressed() ||
      this._mouseDownButton !== EMouseButton.EMPTY ||
      this._isComplete
    )
      return;

    this._isComplete = true;
    this.applySelected();
    this.dispose();
    this.exit();
  };

  override dispose() {
    this._hoverBoxes.clear();
    this._selectedBoxes.clear();
    this._multiModelData = null;
    this._gs.editorStateStore.clearHoverEntity();
    this._disposeSet.forEach((cb) => cb());
    this._disposeSet.clear();
    // 退出多选，面板改回按 selected 取数
    editorEvent.emit(EditorEventName.MULTI_SELECT_DATA_CHANGE, null);
    super.dispose();
  }
}
